import { PageLayout } from "@/components/page-layout/PageLayout";
import { SpacingValues } from "@/lib/utils/SpacingValues";
import { CustomerInfoCard } from "@/features/product-and-price/info-boxes/customer/CustomerInfoCard";
import { CustomerThreeYearStatisticsInfoBox } from "@/features/product-and-price/info-boxes/customer/statistics/CustomerThreeYearStatisticsInfoBox";
import { DocumentsInfoBox } from "@/features/product-and-price/info-boxes/document/DocumentsInfoBox";
import { TopHeadAgreementsInfoBox } from "@/features/product-and-price/info-boxes/head-agreement/top-head-agreements/TopHeadAgreementsInfoBox";
import { Box, Row, Spacing } from "@stenajs-webui/core";
import { ErrorScreen, LoadingPanel } from "@stenajs-webui/panels";
import { Helmet } from "react-helmet";
import { useParams } from "react-router";
import { useCustomerDetailsView } from "@/features/product-and-price/customers/customer-details/hooks/useCustomerDetails";
import { AgreedVolumeSummaryCard } from "@/features/product-and-price/agreed-volume/components/AgreedVolumeSummaryCard";
import { stenaBusinessAgreement } from "@stenajs-webui/elements";
import { PageHeader } from "@/components/page-header/PageHeader";

export interface CustomerScreenParams {
  customerId: string;
}

const CUSTOMER_CARD_WIDTH = "340px";
const TABLE_CARDS_MAX_WIDTH = "1400px";

/**
 * The three year statistics table is the widest thing on this screen and its
 * columns carry their own minimums (160px for negotiation status, 100-130px
 * for the seven others), so it stops compressing at roughly 930px and pushes
 * out of its card instead. Preferring a bit more than that lets the row wrap
 * on a narrow window, which drops the cards below the customer card at full
 * width rather than squeezing them past what the table can give up.
 */
const TABLE_CARDS_BASE_WIDTH = "980px";

export const CustomerScreen = () => {
  const numberOfAgreementsToShow = 3;
  const numberOfDocumentsToShow = 3;
  const { customerId } = useParams() as { customerId: string };
  const { loading, customer } = useCustomerDetailsView(
    customerId,
    numberOfAgreementsToShow,
    numberOfDocumentsToShow
  );
  const headAgreements = customer?.topHeadAgreements ?? [];
  const agreementDocuments = customer?.topAgreementDocuments ?? [];
  const rebateAgreementDocuments = customer?.topRebateAgreementDocuments ?? [];
  const currencyCode = customer?.invoiceCurrency.code;

  if (!customerId) {
    return <ErrorScreen text={"Missing custIndex."} />;
  }

  return (
    <>
      <Helmet title="Customer - FPP" />
      <PageHeader icon={stenaBusinessAgreement} title={customer?.name ?? ""} />
      <PageLayout>
        {loading ? (
          <Spacing num={SpacingValues.TWENTYFOUR}>
            <LoadingPanel />
          </Spacing>
        ) : (
          <>
            <Row gap={3} alignItems={"flex-start"} flexWrap={"wrap"}>
              {/**
               * The value table inside the card is width:100%, so the card
               * cannot size itself to its content. The column is what holds it
               * to the width the labels and values actually need, and it must
               * not shrink below that when the row gets tight.
               */}
              <Box width={CUSTOMER_CARD_WIDTH} flexShrink={0}>
                {customer && <CustomerInfoCard customer={customer} />}
              </Box>
              {/**
               * Everything else is stacked in the column beside the customer
               * card, so every card shares the column width and the stack
               * starts alongside the customer card rather than below it.
               */}
              <Box
                flexGrow={1}
                flexShrink={1}
                flexBasis={TABLE_CARDS_BASE_WIDTH}
                minWidth={0}
                maxWidth={TABLE_CARDS_MAX_WIDTH}
                gap={3}
              >
                <TopHeadAgreementsInfoBox
                  headAgreements={headAgreements}
                  customerId={customerId}
                />
                <AgreedVolumeSummaryCard customerId={customerId} />
                <DocumentsInfoBox
                  agreementDocuments={agreementDocuments}
                  rebateAgreementDocuments={rebateAgreementDocuments}
                  customerNumber={customerId}
                />
                <CustomerThreeYearStatisticsInfoBox
                  currencyCode={currencyCode ?? ""}
                  customerId={customerId}
                />
              </Box>
            </Row>
          </>
        )}
      </PageLayout>
    </>
  );
};
